import $ from 'jquery';
import {svg} from '../svg.ts';
import {htmlEscape} from 'escape-goat';
import {clippie} from 'clippie';
import {showTemporaryTooltip} from '../modules/tippy.ts';
import {GET, POST} from '../modules/fetch.ts';
import {showErrorToast} from '../modules/toast.ts';
import {createElementFromHTML, createElementFromAttrs} from '../utils/dom.ts';
import {isImageFile, isVideoFile} from '../utils.ts';
import Dropzone from 'dropzone';
import type {DropzoneFile, DropzoneOptions} from 'dropzone';
import ignore from 'ignore';
import {event} from '../analytics.ts';

const {csrfToken, i18n, pageData = {}} = window.config;

type CustomDropzoneFile = DropzoneFile & {uuid: string};

// dropzone has its owner event dispatcher (emitter)
export const DropzoneCustomEventReloadFiles = 'dropzone-custom-reload-files';
export const DropzoneCustomEventRemovedFile = 'dropzone-custom-removed-file';
export const DropzoneCustomEventUploadDone = 'dropzone-custom-upload-done';

// list of file patterns not to upload when uploading a directory
const UPLOAD_IGNORE_LIST = [
  /\.git\//,
  /\.DS_Store$/,
];

const FILENAME_PREVIEW_TEMPLATE = `
<div class="dz-preview dz-file-preview dz-file-list-preview">
  <div class="dz-success-mark"><span>✔</span></div>
  <div class="dz-error-mark"><span>✘</span></div>
  <div class="dz-details">
    <div class="dz-filename"><span data-dz-name></span> (<span class="dz-size" data-dz-size></span>)<span class="dz-upload-progress-text"></span></div>
  </div>
  <div class="dz-error-message"><span data-dz-errormessage></span></div>
</div>
`;

function isIgnoredFilePath(filePath: string) {
  for (const regex of UPLOAD_IGNORE_LIST) {
    if (regex.test(filePath)) {
      return true;
    }
  }
  return false;
}

async function createDropzone(el: HTMLElement, opts: DropzoneOptions) {
  // Dropzone itself is imported statically (its constants are used at module load),
  // so only the stylesheet needs a dynamic import here.
  await import('dropzone/dist/dropzone.css');
  return new Dropzone(el, opts);
}

export function generateMarkdownLinkForAttachment(file: Partial<CustomDropzoneFile>, {width, dppx }: {width?: number, dppx?: number} = {}) {
  let fileMarkdown = `[${file.name}](/attachments/${file.uuid})`;
  if (isImageFile(file)) {
    fileMarkdown = `!${fileMarkdown}`;
    if (width > 0 && dppx > 1) {
      // Scale down images from HiDPI monitors. This uses the <img> tag because it's the only
      // method to change image size in Markdown that is supported by all implementations.
      // Make the image link relative to the repo path, then the final URL is "/sub-path/owner/repo/attachments/{uuid}"
      fileMarkdown = `<img width="${Math.round(width / dppx)}" alt="${htmlEscape(file.name)}" src="attachments/${htmlEscape(file.uuid)}">`;
    } else {
      // Markdown always renders the image with a relative path, so the final URL is "/sub-path/owner/repo/attachments/{uuid}"
      // TODO: it should also use relative path for consistency, because absolute is ambiguous for "/sub-path/attachments" or "/attachments"
      fileMarkdown = `![${file.name}](/attachments/${file.uuid})`;
    }
  } else if (isVideoFile(file)) {
    fileMarkdown = `<video src="attachments/${htmlEscape(file.uuid)}" title="${htmlEscape(file.name)}" controls></video>`;
  }
  return fileMarkdown;
}

function addCopyLink(file: Partial<CustomDropzoneFile>) {
  // Create a "Copy Link" element, to conveniently copy the image or file link as Markdown to the clipboard
  // The "<a>" element has a hardcoded cursor: pointer because the default is overridden by .dropzone
  const copyLinkEl = createElementFromHTML(`
<div class="tw:text-center">
  <a href="#" class="tw:cursor-pointer">${svg('octicon-copy', 14)} Copy link</a>
</div>`);
  copyLinkEl.addEventListener('click', async (e) => {
    e.preventDefault();
    const success = await clippie(generateMarkdownLinkForAttachment(file));
    showTemporaryTooltip(e.target as Element, success ? i18n.copy_success : i18n.copy_error);
  });
  file.previewTemplate.append(copyLinkEl);
}

function sortDzList(dzInst: Dropzone) {
  const acceptedFiles = dzInst.getAcceptedFiles() as (DropzoneFile & {fullPath?: string})[];

  const previewMap = Object.fromEntries(acceptedFiles.map((file) => ([file.fullPath, file.previewElement])));
  const sortedPreviewElements = Object.keys(previewMap)
    .sort((a, b) => cmpPaths(a.split('/'), b.split('/')))
    .map((fullPath) => previewMap[fullPath]);
  // re-append them in the correct order
  dzInst.previewsContainer.append(...sortedPreviewElements);
}

function cmpPaths(a: string[], b: string[]): number {
  if (a.length >= 2 && b.length >= 2) {
    return a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : cmpPaths(a.slice(1), b.slice(1));
  } else if (a.length >= 2) {
    return -1;
  } else if (b.length >= 2) {
    return 1;
  }
  return a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0;
}

const UPLOADED_STATUSES = [Dropzone.SUCCESS, Dropzone.ERROR, Dropzone.CANCELED];
function updateProgressBar(dzInst: Dropzone) {
  const progressEl = dzInst.element.querySelector('progress.overall-progress');
  if (!progressEl) {
    return;
  }

  const isUploading = dzInst.getActiveFiles().length > 0;
  if (!isUploading) {
    // no longer uploading: remove the progress bar
    progressEl.removeAttribute('max');
    progressEl.removeAttribute('value');
    progressEl.classList.add('tw:hidden');
  } else {
    // currently uploading: enable the progress bar
    // Note: Progess is calculated by (number of files succeeded/failed/canceled) / (number of files accepted)
    const acceptedFiles = dzInst.getAcceptedFiles();
    const acceptedCount = acceptedFiles.length;
    const uploadedCount = acceptedFiles.filter((file) => UPLOADED_STATUSES.includes(file.status)).length;
    progressEl.setAttribute('max', String(acceptedCount));
    progressEl.setAttribute('value', String(uploadedCount));
    progressEl.classList.remove('tw:hidden');
  }
}

function toggleFormButtons(dzInst: Dropzone) {
  const uploadedCount = dzInst.files.filter((file) => UPLOADED_STATUSES.includes(file.status)).length;
  const hasUploaded = uploadedCount > 0;
  const isUploading = dzInst.getActiveFiles().length > 0;

  const form = dzInst.element.closest('.form');
  const buttons: HTMLButtonElement[] = form ? Array.from(form.querySelectorAll('button:not(.dropzone-add-button)')) : [];
  const disableSubmitWithoutFiles = dzInst.element.hasAttribute('data-disable-submit-without-files');

  for (const button of buttons) {
    button.disabled = isUploading || (disableSubmitWithoutFiles && !hasUploaded);
  }
}

function initAddButton(dzInst: Dropzone, dropzoneEl: HTMLElement) {
  const formEl = dropzoneEl.closest('[data-dropzone-container]');
  if (!formEl) {
    return;
  }
  const addButton = formEl.querySelector('.dropzone-add-button');
  if (!addButton) {
    return;
  }
  $(addButton).dropdown({
    action: 'hide',
  });

  const menuItems = addButton.querySelectorAll('.menu .item[data-upload-type]');
  for (const item of menuItems) {
    item.addEventListener('click', (e) => {
      e.preventDefault();
      const uploadType = (item as HTMLElement).getAttribute('data-upload-type');
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = true;
      if (uploadType === 'folder') {
        input.setAttribute('webkitdirectory', '');
      }
      input.style.display = 'none';
      document.body.append(input);

      let hasChanged = false;
      const cleanup = () => {
        if (input.parentNode) {
          input.remove();
        }
      };

      input.addEventListener('change', () => {
        hasChanged = true;
        const files = Array.from(input.files || []);
        for (const file of files) {
          dzInst.addFile(file as DropzoneFile);
        }
        dzInst.emit('addedfiles', files);
        cleanup();
      });

      // When the file dialog closes (cancel or select), focus returns to the window
      // Use this to clean up the input if no files were selected
      const handleFocus = () => {
        window.removeEventListener('focus', handleFocus);
        // Give change event a chance to fire if it's going to
        setTimeout(() => {
          if (!hasChanged) {
            cleanup();
          }
        }, 0);
      };
      window.addEventListener('focus', handleFocus);

      input.click();
    });
  }
}

type FileUuidDict = Record<string, {submitted: boolean}>;

// File metadata we attach to Dropzone files for the chunked-upload code path.
// Lives on `file` itself rather than a sidecar map so it cleans up with the file.
type ChunkedFileExt = {
  _useChunked?: boolean;
  _finalizeResponse?: {uuid?: string};
};

// postWithRetry is the chunked-upload transport. fetch() handles begin / chunk /
// finalize requests, with a small retry loop for transient network or 5xx errors
// to absorb the kind of blips that motivate chunked uploads in the first place.
// 4xx is treated as a definitive validation error and not retried.
// Exported for unit tests.
export async function postWithRetry(url: string, data: URLSearchParams | FormData, retries = 3): Promise<any> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < retries; attempt++) {
    let resp: Response | undefined;
    try {
      resp = await POST(url, {data});
    } catch (err) {
      // Network-level failure (DNS, abort, offline). Retry.
      lastErr = err;
    }
    if (resp) {
      if (resp.ok) {
        // Some endpoints respond with no body; tolerate that.
        const text = await resp.text();
        return text ? JSON.parse(text) : {};
      }
      if (resp.status >= 400 && resp.status < 500) {
        // Definitive validation error — don't retry, surface immediately.
        const body = await resp.text();
        throw new Error(`${resp.status} ${body || resp.statusText}`);
      }
      lastErr = new Error(`${resp.status} ${resp.statusText}`);
    }
    if (attempt < retries - 1) {
      await new Promise((res) => setTimeout(res, 250 * (attempt + 1)));
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}

// uploadFileChunked drives the begin → chunks → finalize sequence for a single
// file. On success it transitions Dropzone's state machine via `_finished` so
// the existing success handlers run unchanged; on failure it calls
// `_errorProcessing`. Progress is emitted after each chunk completes
// Exported for unit tests.
export async function uploadFileChunked(
  dzInst: Dropzone,
  file: DropzoneFile & {fullPath?: string},
  chunkedBaseUrl: string,
  chunkSize: number,
) {
  // Clamp chunkSize to file.size so a configured chunk size larger than the
  // file (e.g. a 30 MB file with a 100 MB ChunkSize setting) becomes a
  // single-chunk session instead of a server-side 400.
  const effectiveChunkSize = Math.min(chunkSize, file.size);
  const totalChunks = Math.ceil(file.size / effectiveChunkSize);

  const beginResp = await postWithRetry(chunkedBaseUrl, new URLSearchParams({
    filename: file.name,
    full_path: file.fullPath ?? file.name,
    total_size: String(file.size),
    chunk_size: String(effectiveChunkSize),
  }));
  const uuid: string = beginResp.uuid;
  if (!uuid) throw new Error('chunked upload: server returned no uuid');

  for (let i = 0; i < totalChunks; i++) {
    const start = i * effectiveChunkSize;
    const end = Math.min(start + effectiveChunkSize, file.size);
    const blob = file.slice(start, end);
    const form = new FormData();
    form.append('chunkindex', String(i));
    form.append('totalchunkcount', String(totalChunks));
    form.append('totalfilesize', String(file.size));
    form.append('chunksize', String(blob.size));
    form.append('chunkbyteoffset', String(start));
    form.append('file', blob, file.name);
    await postWithRetry(`${chunkedBaseUrl}/${uuid}`, form);
    // Emit progress on chunk completion. Both Dropzone's built-in
    // `.dz-progress` overlay and the folder-mode per-file text handler subscribe
    // to this event and update without further wiring on our side.
    dzInst.emit('uploadprogress', file, (end / file.size) * 100, end);
  }

  const finalizeResp = await postWithRetry(`${chunkedBaseUrl}/${uuid}/finalize`, new URLSearchParams({}));
  // Shape the response for both event-handler conventions: single-file
  // `success` reads `resp.uuid`; folder-mode `successmultiple` reads
  // `resp.uuids[i]`. uploadFileChunked is always called per file (the
  // `_finished` array length is 1), so the two shapes refer to the same id.
  const respForFinished = {uuid: finalizeResp.uuid, uuids: [finalizeResp.uuid]};
  (file as ChunkedFileExt)._finalizeResponse = respForFinished;
  // Dropzone's _finished transitions the file to SUCCESS, emits success +
  // complete, and triggers the next queued upload. Calling it with the
  // finalize response keeps existing event-handler shapes intact.
  (dzInst as any)._finished([file], respForFinished, null);
}

// sessionSizeRejection returns a rejection message when the given files would
// exceed `sessionMaxBytes`, or null otherwise. We re-sum on every accept() rather
// than tracking a running counter so removals and failed uploads don't drift.
// Files in `error` / `canceled` states don't contribute — a rejected upload
// shouldn't permanently occupy quota. Iterates with an early return so we stop
// as soon as the running total crosses the limit.
//
// Exported for unit testing; the size check inside `initDropzone`'s accept()
// callback calls into this.
export function sessionSizeRejection(
  files: ReadonlyArray<{size: number; status?: string}>,
  sessionMaxBytes: number,
  message: string,
): string | null {
  if (sessionMaxBytes <= 0) return null;
  let totalBytes = 0;
  for (const f of files) {
    if (f.status === 'error' || f.status === 'canceled') continue;
    totalBytes += f.size || 0;
    if (totalBytes > sessionMaxBytes) return message;
  }
  return null;
}

// Hide a stale repo-upload flash-error so it doesn't linger past a fresh file selection.
export function clearStaleUploadFlashErrors(uploadType: string | null, dropzoneEl: HTMLElement) {
  if (uploadType !== 'repo') return;
  const container = dropzoneEl.closest('.ui.container');
  if (!container) return;
  for (const el of container.querySelectorAll('.flash-message.flash-error')) {
    el.remove();
  }
}

/**
 * @param {HTMLElement} dropzoneEl
 */
export async function initDropzone(dropzoneEl: HTMLElement) {
  const listAttachmentsUrl = dropzoneEl.closest('[data-attachment-url]')?.getAttribute('data-attachment-url');
  const removeAttachmentUrl = dropzoneEl.getAttribute('data-remove-url');
  const attachmentBaseLinkUrl = dropzoneEl.getAttribute('data-link-url');
  const uploadType = dropzoneEl.getAttribute('data-upload-type');

  // use a reference to the actual element, in case the dropzone gets disconnected during initialization
  const previewsContainer: HTMLElement = dropzoneEl.querySelector('.dropzone-previews');
  const uploadFolders = dropzoneEl.hasAttribute('data-select-folders');
  const fileListMode: 'files' | 'thumbnails' = dropzoneEl.getAttribute('data-file-list-mode') === 'files' ? 'files' : 'thumbnails';
  const gitignoreFilter = pageData.gitignoreContent ? ignore().add(pageData.gitignoreContent) : null;

  let disableRemovedfileEvent = false; // when resetting the dropzone (removeAllFiles), disable the "removedfile" event
  let fileUuidDict: FileUuidDict = {}; // to record: if a comment has been saved, then the uploaded files won't be deleted from server when clicking the Remove in the dropzone

  // Aggregate session-size cap, applied in addition to per-file size and max-file-count.
  // 0 (or missing) disables the check. We re-sum on every accept() rather than
  // maintaining a running counter so file removals and failed uploads don't drift.
  const sessionMaxBytes = Number(dropzoneEl.getAttribute('data-total-max-size') ?? 0) * 1024 * 1024;
  const sessionTooBigMessage = dropzoneEl.getAttribute('data-session-too-big') ?? '';

  // using uploadMultiple to make a semantic distinction between when we're uploading folders or just multiple files
  // that way, if this condition changes, it's easier to know what event handlers might be affected.
  const uploadMultiple = uploadFolders;
  const opts: DropzoneOptions = {
    url: dropzoneEl.getAttribute('data-upload-url'),
    headers: {'X-Csrf-Token': csrfToken},
    acceptedFiles: ['*/*', ''].includes(dropzoneEl.getAttribute('data-accepts')) ? null : dropzoneEl.getAttribute('data-accepts'),
    timeout: null,

    uploadMultiple,
    parallelUploads: uploadMultiple ? 10 : 2,
    ignoreHiddenFiles: false,

    previewsContainer,
    clickable: !uploadFolders, // when uploading folders, we use a separate button outside the dropzone to select file or folder upload

    dictDefaultMessage: uploadFolders ? dropzoneEl.getAttribute('data-folder-message') : dropzoneEl.getAttribute('data-default-message'),
    dictInvalidFileType: dropzoneEl.getAttribute('data-invalid-input-type'),
    dictFileTooBig: dropzoneEl.getAttribute('data-file-too-big'),
    dictRemoveFile: dropzoneEl.getAttribute('data-remove-file'),

    createImageThumbnails: fileListMode === 'thumbnails',
    thumbnailMethod: 'contain',
    thumbnailWidth: 480,
    thumbnailHeight: 480,
    addRemoveLinks: true,

    init() {},
    accept(this: Dropzone, file: DropzoneFile & {fullPath?: string; ignored?: boolean}, done) {
      // we only ignore files if uploaded as part of a directory; if a user is uploading a single file and it's a .DS_Store, we trust that's what they want
      if (uploadFolders && // Ignored flag set during `addedFile` handler
        file.ignored) {
        done('File ignored');
        return;
      }

      // By the time accept() runs, Dropzone has already added `file` to
      // `this.files`, so the helper is summing the post-add total.
      const rejectMsg = sessionSizeRejection(this.files, sessionMaxBytes, sessionTooBigMessage);
      if (rejectMsg !== null) {
        done(rejectMsg);
        return;
      }

      done();
    },
  };

  // if previewTemplate is present at all, it'll be overwritten
  // so we only add it if we need it
  if (uploadFolders) {
    opts.previewTemplate = FILENAME_PREVIEW_TEMPLATE;
  }

  if (dropzoneEl.hasAttribute('data-max-file')) {
    opts.maxFiles = Number(dropzoneEl.getAttribute('data-max-file'));
  }
  if (dropzoneEl.hasAttribute('data-max-size')) {
    opts.maxFilesize = Number(dropzoneEl.getAttribute('data-max-size'));
  }

  // there is a bug in dropzone: if a non-image file is uploaded, then it tries to request the file from server by something like:
  // "http://localhost:3000/owner/repo/issues/[object%20Event]"
  // the reason is that the preview "callback(dataURL)" is assign to "img.onerror" then "thumbnail" uses the error object as the dataURL and generates '<img src="[object Event]">'
  const dzInst = await createDropzone(dropzoneEl, opts);
  if (dzInst === null) {
    return;
  }

  // Chunked uploads (repo upload type only). The server publishes the threshold
  // and chunk size via data-* attributes; if either is 0 or absent, the chunked
  // path stays dormant and uploads fall through to the existing single-POST
  // flow.
  const chunkedUploadThreshold = Number(dropzoneEl.getAttribute('data-chunked-upload-threshold') ?? 0);
  const chunkSize = Number(dropzoneEl.getAttribute('data-chunk-size') ?? 0);
  const chunkedBaseUrl = dropzoneEl.getAttribute('data-chunked-upload-url');
  const chunkingEnabled = uploadType === 'repo' && chunkedUploadThreshold > 0 && chunkSize > 0 && Boolean(chunkedBaseUrl);
  if (chunkingEnabled) {
    // Flag oversize files at add time. We can't make this decision in
    // uploadFiles alone because Dropzone may batch a flagged file with
    // un-flagged ones; flagging here lets us partition the batch later.
    dzInst.on('addedfile', (file: DropzoneFile & ChunkedFileExt) => {
      if (file.size >= chunkedUploadThreshold) {
        file._useChunked = true;
      }
    });

    // Wrap Dropzone's transport. Small files keep going through the original
    // path (which honors uploadMultiple, batching, headers, etc.); large files
    // go through our chunked routine, which transitions them via `_finished`
    // when done so the rest of the state machine is unaware of the divergence.
    const dzAny = dzInst as any;
    const origUploadFiles = dzAny.uploadFiles.bind(dzInst);
    dzAny.uploadFiles = (files: (DropzoneFile & ChunkedFileExt)[]) => {
      const chunked = files.filter((f) => f._useChunked);
      const normal = files.filter((f) => !f._useChunked);
      if (normal.length) origUploadFiles(normal);
      const runChunked = async (f: DropzoneFile & ChunkedFileExt) => {
        try {
          await uploadFileChunked(dzInst, f, chunkedBaseUrl, chunkSize);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          dzAny._errorProcessing([f], msg, null);
        }
      };
      for (const f of chunked) {
        // Each chunked upload runs independently; we intentionally don't await
        // here so Dropzone's batch processing isn't blocked. Errors are
        // funneled through Dropzone's own error transition.
        runChunked(f);
      }
    };
  }

  dzInst.on('successmultiple', (files: (DropzoneFile & {uuid: string})[], resp: any) => {
    for (const [i, file] of files.entries()) {
      const uuid = resp.uuids[i];
      file.uuid = uuid;
      fileUuidDict[uuid] = {submitted: false};
      const input = createElementFromAttrs('input', {name: 'files', type: 'hidden', id: `dropzone-file-${uuid}`, value: uuid});
      dropzoneEl.querySelector('.files').append(input);
      dzInst.emit(DropzoneCustomEventUploadDone, {file});
    }
  });

  dzInst.on('success', (file: CustomDropzoneFile, resp: any) => {
    if (uploadFolders) {
      return;
    }

    file.uuid = resp.uuid;
    fileUuidDict[file.uuid] = {submitted: false};
    const input = createElementFromAttrs('input', {name: 'files', type: 'hidden', id: `dropzone-file-${resp.uuid}`, value: resp.uuid});
    dropzoneEl.querySelector('.files').append(input);
    addCopyLink(file);
    dzInst.emit(DropzoneCustomEventUploadDone, {file});
  });

  dzInst.on('removedfile', async (file: CustomDropzoneFile) => {
    if (disableRemovedfileEvent) return;

    dzInst.emit(DropzoneCustomEventRemovedFile, {fileUuid: file.uuid});
    document.querySelector(`#dropzone-file-${file.uuid}`)?.remove();
    // when the uploaded file number reaches the limit, there is no uuid in the dict, and it doesn't need to be removed from server
    if (removeAttachmentUrl && fileUuidDict[file.uuid] && !fileUuidDict[file.uuid].submitted) {
      await POST(removeAttachmentUrl, {data: new URLSearchParams({file: file.uuid})});
    }
  });

  dzInst.on('submit', () => {
    for (const fileUuid of Object.keys(fileUuidDict)) {
      fileUuidDict[fileUuid].submitted = true;
    }
  });

  // Ignored files UI: track deduplicated paths and show an expandable summary
  const ignoredFilePaths = new Set<string>();
  let ignoredFilesEl: HTMLDetailsElement | null = null;
  let ignoredFilesUpdateTimer: ReturnType<typeof setTimeout> | null = null;

  // Schedule an update on the next tick to let batches of ignored files accumulate instead of
  // updating the DOM on every single one.
  function scheduleIgnoredFilesUIUpdate() {
    if (ignoredFilesUpdateTimer !== null) return;
    ignoredFilesUpdateTimer = setTimeout(() => {
      ignoredFilesUpdateTimer = null;
      updateIgnoredFilesUI();
    }, 0);
  }

  // When file list is cleared, also remove the ignored files UI
  dzInst.on('reset', () => {
    ignoredFilePaths.clear();
    if (ignoredFilesUpdateTimer !== null) {
      clearTimeout(ignoredFilesUpdateTimer);
      ignoredFilesUpdateTimer = null;
    }
    ignoredFilesEl?.remove();
    ignoredFilesEl = null;
  });

  function updateIgnoredFilesUI() {
    const count = ignoredFilePaths.size;
    if (count === 0) return;

    if (!ignoredFilesEl) {
      ignoredFilesEl = document.createElement('details');
      ignoredFilesEl.classList.add('dropzone-ignored-files');
      dropzoneEl.closest('[data-dropzone-container]')?.append(ignoredFilesEl);
    }

    const wasOpen = ignoredFilesEl.open;
    const sorted = Array.from(ignoredFilePaths).sort((a, b) => cmpPaths(a.split('/'), b.split('/')));
    ignoredFilesEl.innerHTML = `<summary>${svg('octicon-info', 14)} ${count} file${count === 1 ? ' was' : 's were'} ignored based on your project's .gitignore settings. <span class="dropzone-ignored-files-hint">Click to view.</span></summary>` +
      `<ul class="dropzone-ignored-files-list">${sorted.map((p) => `<li>${htmlEscape(p)}</li>`).join('')}</ul>`;
    ignoredFilesEl.open = wasOpen;
  }

  dzInst.on('addedfile', () => clearStaleUploadFlashErrors(uploadType, dropzoneEl));

  let needsSorting = false;
  dzInst.on('addedfile', (file: (DropzoneFile & {fullPath?: string; ignored?: boolean})) => {
    if (!uploadFolders) {
      return;
    }

    // if the file was just dragged-and-dropped by itself, we only get file.name (which we always get)
    let fullPath = file.name;
    if (file.fullPath) {
      // if the file was part of a folder and was uploaded via drag-and-drop, we should have the full path under fullPath, so pull it in here
      // and don't do any path manipulation
      fullPath = file.fullPath;
    } else if (file.webkitRelativePath) {
      // if the file was uploaded via the OS dialog (which does not provide fullPath), we're uploading folder *contents*, so we strip the top directory
      // otherwise, we leave it alone, as the user could have uploaded multiple folders and files at once
      const slashIx = file.webkitRelativePath.indexOf('/', 1);
      fullPath = file.webkitRelativePath.substring(slashIx + 1); // +1 to account for the slash
    }

    // now normalize it so that it's always accessible under fullPath
    file.fullPath = fullPath;

    // If it's an ignored path, mark it for removal
    if (isIgnoredFilePath(fullPath) || gitignoreFilter?.ignores(fullPath)) {
      file.ignored = true;
      ignoredFilePaths.add(fullPath);
      scheduleIgnoredFilesUIUpdate();
      return;
    }

    // have the full relative path show up in the template
    for (const el of file.previewElement.querySelectorAll('[data-dz-name]')) {
      el.textContent = file.fullPath;
    }

    needsSorting = true;
  });

  // The `addedfiles` event is not fired reliably after all the files get added (it gets fired first for drag and drop)
  // so the best we can do in knowing when we should sort the list is when processing starts (which is right after
  // files are finished getting added). But we should only sort when a new file has been added, for perf reasons.
  dzInst.on('processingmultiple', () => {
    updateProgressBar(dzInst);
    toggleFormButtons(dzInst);

    if (needsSorting) {
      needsSorting = false;
      sortDzList(dzInst);
    }
  });

  dzInst.on('sendingmultiple', (files: (DropzoneFile & {fullPath?: string})[], _xhr, formData) => {
    if (!uploadFolders) {
      return;
    }

    for (const [i, file] of files.entries()) {
      formData.append(`fullPath[${i}]`, file.fullPath);
    }
  });

  dzInst.on('completemultiple', () => {
    if (uploadMultiple) {
      updateProgressBar(dzInst);
      toggleFormButtons(dzInst);
    }
  });

  dzInst.on('processing', (file: DropzoneFile & {uploadStartTime?: number}) => {
    file.uploadStartTime = performance.now();

    if (!uploadMultiple) {
      toggleFormButtons(dzInst);
    }
  });

  // When uploading folders, keep basic progress next to each file since the overall progress
  // bar only updates when each of them finishes.
  if (uploadFolders) {
    dzInst.on('uploadprogress', (file: DropzoneFile, progress: number) => {
      const progressElem = file.previewElement.querySelector('.dz-upload-progress-text');
      if (progressElem) {
        progressElem.textContent = ` - ${Math.round(progress)}%`;
      }
    });
  }

  dzInst.on('complete', () => {
    if (!uploadMultiple) {
      toggleFormButtons(dzInst);
    }
  });

  dzInst.on(DropzoneCustomEventReloadFiles, async () => {
    try {
      const resp = await GET(listAttachmentsUrl);
      const respData = await resp.json();
      // do not trigger the "removedfile" event, otherwise the attachments would be deleted from server
      disableRemovedfileEvent = true;
      dzInst.removeAllFiles(true);
      disableRemovedfileEvent = false;

      dropzoneEl.querySelector('.files').innerHTML = '';
      for (const el of dropzoneEl.querySelectorAll('.dz-preview')) el.remove();
      fileUuidDict = {};
      for (const attachment of respData) {
        const file = {name: attachment.name, uuid: attachment.uuid, size: attachment.size};
        dzInst.emit('addedfile', file);
        dzInst.emit('complete', file);
        if (isImageFile(file.name)) {
          const imgSrc = `${attachmentBaseLinkUrl}/${file.uuid}`;
          dzInst.emit('thumbnail', file, imgSrc);
        }
        addCopyLink(file); // it is from server response, so no "type"
        fileUuidDict[file.uuid] = {submitted: true};
        const input = createElementFromAttrs('input', {name: 'files', type: 'hidden', id: `dropzone-file-${file.uuid}`, value: file.uuid});
        dropzoneEl.querySelector('.files').append(input);
      }
      if (!dropzoneEl.querySelector('.dz-preview')) {
        dropzoneEl.classList.remove('dz-started');
      }
    } catch (error) {
      // TODO: if listing the existing attachments failed, it should stop from operating the content or attachments,
      //  otherwise the attachments might be lost.
      showErrorToast(`Failed to load attachments: ${error}`);
      console.error(error);
    }
  });

  dzInst.on('error', (file: DropzoneFile & {uploadStartTime?: number, ignored?: boolean}, message: string, xhr?: XMLHttpRequest) => {
    // Ignore errors for files that were filtered by ignore list as they will be
    // silently removed
    if (file.ignored) {
      dzInst.removeFile(file);
      return;
    }
    if (file.status !== Dropzone.CANCELED) {
      console.error(file, message, xhr);
      if (xhr && xhr.status === 0) {
        message = 'Upload interrupted — your network connection appears to be unstable. Please check your connection and try again.';
      } else {
        message = `Upload error: ${message}`;
      }
      showErrorToast(message);

      event('file_upload_error', {
        file_size: file.size,
        upload_duration: file.uploadStartTime ? Math.round(performance.now() - file.uploadStartTime) : undefined,
        error_message: message,
        upload_type: uploadType,
      });
    }

    // if we're uploading a folder, leave the file there so the user can see the message
    if (!uploadFolders) {
      dzInst.removeFile(file);
    }
  });

  toggleFormButtons(dzInst);

  if (listAttachmentsUrl) {
    dzInst.emit(DropzoneCustomEventReloadFiles);
  }

  if (uploadFolders) {
    initAddButton(dzInst, dropzoneEl);
  }
  return dzInst;
}
